import {
  GroupBy,
  MetricsType,
  UtilityConsumption,
  Metrics,
  ReadingType,
} from "./types";

/**
 * Accumulate consumption into a group
 * @param group group name
 * @param acc the accumulator
 * @param consumption current consumption of utility datum
 * @param topKey top level key of grouping
 * @param downKey down level key of grouping
 */
function accumulate(
  group: GroupBy,
  acc: UtilityConsumption,
  consumption: number,
  topKey: string,
  downKey: string
) {
  const top = acc[group] && acc[group][topKey];
  const down = top && acc[group][topKey][downKey];

  return {
    ...acc[group],
    [topKey]: {
      ...top,
      [downKey]: consumption + (down || 0),
    },
  };
}

/**
 * Retrieve accumulated consumption in different groups
 * @param data
 * @param metricsType
 * @param filterBy filter sites by siteIds
 */
export function getConsumption(
  data: Metrics,
  metricsType: MetricsType,
  readingType: ReadingType = ReadingType.All,
  filterBy: string[] = []
): UtilityConsumption {
  const sites = data[metricsType] ? Object.entries(data[metricsType]) : [];
  return sites
    .filter(([siteId]) => !filterBy.length || filterBy.includes(siteId))
    .reduce(
      (acc: UtilityConsumption, [siteId, dates]) => {
        Object.entries(dates).forEach(([date, datum]) => {
          let [year, month] = date.split("-");
          month = month.replace(/^0+/, ""); // Remove leading zero from month

          const consumption = datum.reduce((sum, item) => {
            const shouldAdd =
              readingType === ReadingType.All || readingType === item.type;
            return shouldAdd ? sum + item.consumption : sum;
          }, 0);

          // Accumulating consumption in various groups
          // This has been done to feed different kind of charts easily and quickly
          acc["SiteInMonths"] = accumulate(
            "SiteInMonths",
            acc,
            consumption,
            siteId,
            month
          );
          acc["SiteInYears"] = accumulate(
            "SiteInYears",
            acc,
            consumption,
            siteId,
            year
          );
          acc["YearInMonths"] = accumulate(
            "YearInMonths",
            acc,
            consumption,
            year,
            month
          );
          acc["YearInSites"] = accumulate(
            "YearInSites",
            acc,
            consumption,
            year,
            siteId
          );
          acc["MonthInSites"] = accumulate(
            "MonthInSites",
            acc,
            consumption,
            month,
            siteId
          );
          acc["MonthInYears"] = accumulate(
            "MonthInYears",
            acc,
            consumption,
            month,
            year
          );
          acc["AllMonthInSites"] = accumulate(
            "AllMonthInSites",
            acc,
            consumption,
            date,
            siteId
          );
        });

        return acc;
      },
      {
        SiteInMonths: {},
        SiteInYears: {},
        YearInMonths: {},
        YearInSites: {},
        MonthInSites: {},
        MonthInYears: {},
        AllMonthInSites: {}
      }
    );
}
